import { motion } from 'framer-motion';
import { ChevronLeft, Check, ArrowRight } from 'lucide-react';

const PillTrack = ({ total, current }) => (
  <div className="flex items-center justify-center gap-2">
    {Array.from({ length: total }).map((_, i) => {
      let cls = 'bg-[#1f3d2b]/15 w-3 h-3';
      if (i < current) cls = 'bg-[#1f3d2b]/55 w-3 h-3';
      if (i === current) cls = 'bg-[#1f3d2b] w-10 h-3';
      return (
        <span
          key={i}
          className={`rounded-full transition-all duration-500 ease-out ${cls}`}
        />
      );
    })}
  </div>
);

const QuestionScreen = ({
  question, stepIndex, totalSteps, answers,
  onAnswer, onMultiToggle, onMultiContinue, onBack,
}) => {
  const q = question;
  const isMulti = !!q.multi;
  const selectedMulti = Array.isArray(answers[q.id]) ? answers[q.id] : [];
  const selectedSingle = answers[q.id];

  const stagger = {
    animate: { transition: { staggerChildren: 0.04, delayChildren: 0.1 } },
  };
  const item = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
  };

  return (
    <div className="flex-1 flex flex-col px-6 sm:px-10 pt-12 pb-8 max-w-4xl mx-auto w-full items-center text-center">
      <div className="flex flex-col items-center gap-3 mb-10">
        <PillTrack total={totalSteps} current={stepIndex} />
        <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#5a7062]">
          Step {stepIndex + 1} of {totalSteps}
        </div>
      </div>

      <motion.h2
        key={`prompt-${q.id}`}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        data-testid="kiosk-question-prompt"
        className="text-4xl sm:text-5xl text-[#0d1f15] mb-12 leading-[1.1] tracking-tight max-w-2xl"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
      >
        {q.prompt}
      </motion.h2>

      {isMulti && selectedMulti.length === 0 && q.options.length === 0 && (
        <p className="text-sm text-[#5a7062] mb-6">Loading options…</p>
      )}

      <motion.div
        key={`opts-${q.id}`}
        variants={stagger}
        initial="initial"
        animate="animate"
        className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full"
      >
        {q.options.map((opt) => {
          const isSelected = isMulti
            ? selectedMulti.includes(opt.value)
            : selectedSingle === opt.value;
          return (
            <motion.button
              variants={item}
              key={opt.value}
              data-testid={`kiosk-option-${q.id}-${opt.value}`}
              onClick={() => isMulti
                ? onMultiToggle(q.id, opt.value)
                : onAnswer(q.id, opt.value)}
              whileTap={{ scale: 0.97 }}
              className={`text-left rounded-3xl px-6 py-6 sm:py-7 text-lg sm:text-xl transition-colors duration-200 flex items-center justify-between gap-4 border ${
                isSelected
                  ? 'bg-[#1f3d2b] text-white border-[#1f3d2b] shadow-lg shadow-[#1f3d2b]/25'
                  : 'bg-white/80 backdrop-blur-md border-[#1f3d2b]/12 text-[#0d1f15] active:bg-[#1f3d2b]/[0.04]'
              }`}
              style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600, minHeight: 88 }}
            >
              <span className="flex-1 leading-snug">{opt.label}</span>
              <span
                className={`flex-shrink-0 w-7 h-7 flex items-center justify-center transition-colors border-2 ${
                  isMulti ? 'rounded-md' : 'rounded-full'
                } ${
                  isSelected
                    ? 'border-white bg-white/20'
                    : 'border-[#1f3d2b]/25 bg-transparent'
                }`}
              >
                {isSelected && <Check size={14} strokeWidth={3} className="text-white" />}
              </span>
            </motion.button>
          );
        })}
      </motion.div>

      {isMulti && (
        <button
          data-testid="kiosk-multi-continue"
          onClick={() => onMultiContinue(q.id)}
          className="mt-8 bg-[#1f3d2b] text-white rounded-full px-10 py-4 text-base tracking-tight shadow-lg shadow-[#1f3d2b]/20 active:bg-[#152e20] transition-colors inline-flex items-center gap-2"
          style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, minHeight: 56 }}
        >
          {selectedMulti.length > 0
            ? `Continue (${selectedMulti.length} selected)`
            : 'None of these · Continue'}
          <ArrowRight size={16} strokeWidth={2.5} />
        </button>
      )}

      {stepIndex > 0 && (
        <button
          data-testid="kiosk-back-button"
          onClick={onBack}
          className="mt-10 inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-[0.2em] text-[#5a7062] active:text-[#0d1f15] transition-colors py-2 px-3"
        >
          <ChevronLeft size={16} /> Back
        </button>
      )}
    </div>
  );
};

export default QuestionScreen;
